import { useEffect, useState } from 'react'
import api from '../services/api'

interface AuthedImageProps {
  /** API path relative to the client's base URL, e.g. /admin/collectors/:id/documents/license */
  path: string
  alt: string
  className?: string
}

/**
 * Renders an image that requires the admin token to fetch.
 *
 * KYC documents are served by an authenticated endpoint rather than a public
 * signed URL, so a plain <img src> cannot load them — the browser would send no
 * Authorization header. This fetches the bytes through the API client and
 * renders them from an object URL, revoking it on unmount so the blobs do not
 * accumulate while an admin scrolls a list of applications.
 */
export default function AuthedImage({ path, alt, className }: AuthedImageProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let created: string | null = null

    setObjectUrl(null)
    setFailed(false)

    api
      .get(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return
        created = URL.createObjectURL(res.data as Blob)
        setObjectUrl(created)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [path])

  // Both placeholders stand in for the image itself, so they carry an image role
  // and label — a bare div would be skipped entirely by a screen reader.
  if (failed) {
    return (
      <div
        role="img"
        aria-label={`${alt} — unavailable`}
        className={`${className ?? ''} flex items-center justify-center bg-gray-100 text-[10px] text-orange-500 italic`}
      >
        Unavailable
      </div>
    )
  }

  if (!objectUrl) {
    return (
      <div
        role="img"
        aria-label={`${alt} — loading`}
        aria-busy="true"
        className={`${className ?? ''} bg-gray-100 animate-pulse`}
      />
    )
  }

  return <img src={objectUrl} alt={alt} className={className} />
}
